import React, { useEffect, useState } from 'react';
import { ImageIcon, Star, Trash2, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { TarjetaDeAgregarFoto } from './AbanicoDeFotos';
// Cliente Supabase COMPARTIDO (lib/supabase). Antes este componente creaba su
// propio `createClient`, lo que generaba el warning "Multiple GoTrueClient
// instances" y contención de auth (dos clientes con la misma storage key
// pisándose el token) → fetches / firmado de signed URLs lentos o colgados.
import { supabase } from '../lib/supabase';
import { MAX_IMAGENES_POR_PRODUCTO } from '../lib/limiteImagenesProducto';
import CachedImg from './CachedImg';
import { firmarEnLote } from '../utils/firmarEnLote';
import { getCached, CACHE_KEYS } from '../utils/sessionCache';
import VisorDeFoto from '../pages/lanzamientos/VisorDeFoto';

interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
  variante_id?: string;
}

interface VariantImageManagerProps {
  variantId: string;
  images: ProductImage[];
  uploadingImages: Record<string, boolean>;
  imageError: string | null;
  /** Recibe TODOS los archivos elegidos en el picker (multi-selección). Cada
   *  implementación los sube secuencialmente: en paralelo colisionarían
   *  `orden` / `principal`, que se calculan a partir del largo actual. */
  onUpload: (variantId: string, files: File[]) => void | Promise<void>;
  onDelete: (variantId: string, imageId: number) => void;
  onSetPrincipal: (variantId: string, imageId: number) => void;
  onReorder?: (variantId: string, imageId: number, direction: 'up' | 'down') => void;
  showHeader?: boolean;
}

/**
 * La empresa del usuario, para armar los paths del bucket.
 *
 * Primero el cache de sesión —el shell y la sidebar ya lo llenaron al arrancar—, y sólo
 * si no está sale a buscarlo. `company_profiles` queda como respaldo para los perfiles
 * viejos que tienen `company_id` en vez de `id_company`.
 */
const resolverIdCompany = async (): Promise<string | null> => {
  const cacheado = getCached<{ id_company?: string | null }>(CACHE_KEYS.USER_PROFILE);
  if (cacheado?.id_company) return cacheado.id_company;
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: profile } = await supabase
      .from('profiles')
      .select('id_company, company_id')
      .eq('id', user.id)
      .single();
    const idCompany = (profile as any)?.id_company as string | null;
    if (idCompany) return idCompany;
    if ((profile as any)?.company_id) {
      const { data: comp } = await supabase
        .from('company_profiles')
        .select('id_company')
        .eq('id', (profile as any).company_id)
        .single();
      return ((comp as any)?.id_company as string | null) ?? null;
    }
    return null;
  } catch (error) {
    console.error('No se pudo resolver la empresa para firmar las fotos:', error);
    return null;
  }
};

/** `<empresa>/<variante>/<opt|full>/<archivo>` — el path del objeto en `productos`. */
const rutaDeImagen = (
  img: ProductImage,
  idCompany: string,
  variantId: string,
  carpeta: 'opt' | 'full',
): string => {
  const variante = (img.variante_id ||
    (img.producto_variante_id ? String(img.producto_variante_id) : variantId)) as string;
  const partes = img.url.split('/');
  const archivo = partes[partes.length - 1];
  return `${idCompany}/${variante}/${carpeta}/${archivo}`;
};

/** ¿Los dos mapas de firmas dicen lo mismo? */
const mismasUrls = (
  a: Record<number, string>,
  b: Record<number, string>,
): boolean => {
  const clavesA = Object.keys(a);
  if (clavesA.length !== Object.keys(b).length) return false;
  return clavesA.every((k) => a[Number(k)] === b[Number(k)]);
};

const VariantImageManager: React.FC<VariantImageManagerProps> = ({
  variantId,
  images,
  uploadingImages,
  imageError,
  onUpload,
  onDelete,
  onSetPrincipal,
  onReorder,
  showHeader = true
}) => {
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLastImageWarning, setShowLastImageWarning] = useState(false);
  // La posición de la foto abierta en el visor, para poder pasar a la de al lado.
  const [fotoAmpliada, setFotoAmpliada] = useState<number | null>(null);
  
  // Effect to refresh signed URLs before they expire
  useEffect(() => {
    // Set a timer to refresh URLs after 50 minutes (before 60-min expiration)
    const refreshTimer = setTimeout(() => {
      setLastRefreshed(new Date()); // This will trigger the URL generation effect
    }, 50 * 60 * 1000); // 50 minutes
    
    return () => clearTimeout(refreshTimer);
  }, [lastRefreshed]);

  /**
   * Las URLs firmadas de las fotos — EN LOTE y con la empresa resuelta una sola vez.
   *
   * Antes esto firmaba de a una adentro de un `for`, y por CADA foto hacía
   * `auth.getUser()` + la query de `profiles` + (a veces) la de `company_profiles`
   * antes de pedir la firma: cuatro vueltas al servidor por imagen, en serie. Con dos
   * fotos eran los "unos segundos" en los que la tarjeta mostraba el ícono de imagen
   * rota (ADR-2026-09-11-las-fotos-del-editor-de-producto-no-parpadean).
   *
   * Ahora: la empresa se resuelve una vez (del cache de sesión, si está), y las firmas
   * salen en UN pedido por carpeta con `firmarEnLote` —que además cachea por path, así
   * que reabrir el mismo producto no vuelve a la red—.
   *
   * Se prefiere `opt/` y se cae a `full/` sólo para las que no existen optimizadas: es
   * una miniatura de 160px de alto, no hace falta el original.
   */
  useEffect(() => {
    let cancelado = false;

    const firmar = async () => {
      if (!images || images.length === 0) return;

      // Las legacy ya vienen con una URL http de verdad: se muestran tal cual.
      const resultado: Record<number, string> = {};
      for (const img of images) {
        if (img.url?.startsWith('http')) resultado[img.id] = img.url;
      }
      const aFirmar = images.filter((img) => img.url && !img.url.startsWith('http'));

      if (aFirmar.length > 0) {
        const idCompany = await resolverIdCompany();
        if (cancelado) return;
        if (idCompany) {
          const optDe = (img: ProductImage) => rutaDeImagen(img, idCompany, variantId, 'opt');
          const fullDe = (img: ProductImage) => rutaDeImagen(img, idCompany, variantId, 'full');

          const opt = await firmarEnLote(aFirmar.map(optDe), 'productos');
          if (cancelado) return;
          const sinOpt = aFirmar.filter((img) => !opt.has(optDe(img)));
          const full =
            sinOpt.length > 0
              ? await firmarEnLote(sinOpt.map(fullDe), 'productos')
              : new Map<string, string>();
          if (cancelado) return;

          for (const img of aFirmar) {
            const url = opt.get(optDe(img)) ?? full.get(fullDe(img));
            if (url) resultado[img.id] = url;
            else console.warn('No se pudo firmar la imagen del producto:', img.url);
          }
        }
      }

      if (cancelado) return;
      // Mismo mapa = mismo objeto: `images` puede ser un array nuevo en cada render del
      // padre, y un `setState` con un objeto nuevo cada vez es un ciclo de renders.
      setSignedUrls((prev) => (mismasUrls(prev, resultado) ? prev : resultado));
    };

    void firmar();
    return () => {
      cancelado = true;
    };
  }, [images, lastRefreshed, variantId]);

  // Los productos cargados antes del tope pueden tener más de 4: conservan sus
  // fotos, pero no pueden sumar. Por eso `>=` y no `===`.
  const topeAlcanzado = images.length >= MAX_IMAGENES_POR_PRODUCTO;

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // `Array.from` ANTES de limpiar el input: al setear `value = ''` el
    // FileList del evento queda vacío.
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ''; // Reset input
    if (picked.length === 0) return;
    void onUpload(variantId, picked);
  };

  // Estilo de Mi marca, igual que el alta (ADR-2026-10-06-mi-marca-alta-de-producto-con-su-cara):
  // fotos cuadradas `rounded-2xl` con las acciones encima y "Agregar otra foto" como
  // última tarjeta de la grilla.
  const subiendo = !!uploadingImages[variantId];
  const botonSobreFoto =
    'inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-black/70 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all hover:bg-white active:scale-90 disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className={showHeader ? 'mt-4' : ''}>
      {showHeader && (
        <p className="mb-2 px-0.5 text-[13px] font-semibold text-[#16181D]">
          Imágenes
        </p>
      )}

      {/* Multi-selección: se pueden elegir varias fotos del MISMO producto
          (distintos ángulos) de una vez. `accept` solo con MIME types —
          mezclarlos con extensiones (`.heic`) hace que iOS Safari y algunos
          pickers de Android ignoren `multiple` y vuelvan a selección única. */}
      <input
        type="file"
        id={`image-upload-${variantId}`}
        accept="image/*,image/heic,image/heif"
        multiple
        className="hidden"
        // El `pointer-events-none` del label tapa el click pero no el teclado.
        disabled={topeAlcanzado}
        onChange={handleUpload}
      />

      {imageError && (
        <div className="mb-3 flex items-start gap-2 rounded-2xl bg-error-soft px-3.5 py-3 text-[13px] text-error-text">
          <AlertCircle className="mt-px h-4 w-4 shrink-0" />
          <span>{imageError}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {images.map((image, imgIndex) => (
          <div
            key={image.id}
            className="relative aspect-square overflow-hidden rounded-2xl bg-black/[0.04]"
          >
            {/* Hasta que la firma no está NO hay `<img>`: con el nombre del
                archivo en el `src` el navegador dibujaba su ícono de imagen rota
                más el `alt` encima de la tarjeta, y eso era lo que se veía por
                unos segundos al abrir el editor
                (ADR-2026-09-11-las-fotos-del-editor-de-producto-no-parpadean).
                `CachedImg` se encarga del resto: pixel transparente sobre fondo
                gris mientras baja, y la imagen cacheada al reabrir. */}
            {signedUrls[image.id] ? (
              <CachedImg
                src={signedUrls[image.id]}
                alt="Imagen de producto"
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon size={20} className="text-black/20" />
              </div>
            )}
            {/* Borde encima de la foto (un ring del contenedor queda debajo de la imagen). */}
            <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
            {/* Tocar la foto la abre en grande (ADR-2026-10-30-las-fotos-del-producto-se-ven-en-grande).
                Va debajo de las acciones —que vienen después en el DOM— y encima de la foto. */}
            {signedUrls[image.id] && (
              <button
                type="button"
                onClick={() => setFotoAmpliada(imgIndex)}
                aria-label="Ver la foto en grande"
                className="absolute inset-0 cursor-zoom-in rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary"
              />
            )}
            {image.principal && (
              <span className="pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                <Star className="h-3 w-3 fill-current" />
                Principal
              </span>
            )}
            <div className="absolute right-2 top-2 flex gap-1.5">
              {!image.principal && (
                <button
                  type="button"
                  onClick={() => onSetPrincipal(variantId, image.id)}
                  title="Usar como principal"
                  aria-label="Usar como principal"
                  className={`${botonSobreFoto} hover:text-brand-primary`}
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (images.length <= 1) {
                    setShowLastImageWarning(true);
                  } else {
                    setConfirmDeleteId(image.id);
                  }
                }}
                title="Quitar foto"
                aria-label="Quitar foto"
                className={`${botonSobreFoto} hover:text-error`}
                disabled={isDeleting}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Orden: en una grilla "arriba" es "antes" — flechas a los costados. */}
            {onReorder && images.length > 1 && (
              <div className="absolute bottom-2 right-2 flex gap-1.5">
                <button
                  type="button"
                  onClick={() => onReorder(variantId, image.id, 'up')}
                  disabled={imgIndex === 0}
                  className={botonSobreFoto}
                  title="Mover antes"
                  aria-label="Mover antes"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onReorder(variantId, image.id, 'down')}
                  disabled={imgIndex === images.length - 1}
                  className={botonSobreFoto}
                  title="Mover después"
                  aria-label="Mover después"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
        {!topeAlcanzado && (
          <TarjetaDeAgregarFoto
            htmlFor={`image-upload-${variantId}`}
            cargando={subiendo}
            textoCargando="Subiendo…"
          />
        )}
      </div>
      {topeAlcanzado && (
        <p className="mt-3 px-0.5 text-[13px] leading-snug text-black/50">
          Máximo {MAX_IMAGENES_POR_PRODUCTO} fotos. Quitá alguna si querés cambiarla.
        </p>
      )}

      {/* Last Image Warning */}
      {showLastImageWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] animate-modal-appear">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-warning-soft">
              <AlertCircle className="h-5 w-5 text-warning" />
            </div>
            <h3 className="text-[17px] font-semibold text-[#16181D]">No se puede quitar</h3>
            <p className="mt-1 text-[14px] leading-snug text-black/55">
              El producto necesita al menos una foto.
            </p>
            <button
              type="button"
              onClick={() => setShowLastImageWarning(false)}
              className="mt-5 inline-flex h-10 w-full items-center justify-center rounded-full bg-brand-primary px-5 text-[14px] font-medium text-white transition-colors hover:bg-brand-primary-hover"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Image Deletion */}
      {confirmDeleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] animate-modal-appear">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-error-soft">
              <AlertCircle className="h-5 w-5 text-error" />
            </div>
            <h3 className="text-[17px] font-semibold text-[#16181D]">Quitar foto</h3>
            <p className="mt-1 text-[14px] leading-snug text-black/55">
              ¿Seguro que querés quitar esta foto del producto?
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-full border border-black/[0.08] bg-white px-5 text-[14px] font-medium text-[#16181D] transition-colors hover:bg-black/[0.03]"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (confirmDeleteId) {
                    setIsDeleting(true);
                    try {
                      await onDelete(variantId, confirmDeleteId);
                    } finally {
                      setIsDeleting(false);
                      setConfirmDeleteId(null);
                    }
                  }
                }}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-full bg-error px-5 text-[14px] font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Quitando…' : 'Quitar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {fotoAmpliada !== null && images[fotoAmpliada] && (
        <VisorDeFoto
          url={signedUrls[images[fotoAmpliada].id] ?? null}
          onCerrar={() => setFotoAmpliada(null)}
          // Las flechas, con más de una foto (ADR-2026-10-30-el-visor-de-fotos-pasa-de-una-a-otra).
          onAnterior={fotoAmpliada > 0 ? () => setFotoAmpliada(fotoAmpliada - 1) : undefined}
          onSiguiente={fotoAmpliada < images.length - 1 ? () => setFotoAmpliada(fotoAmpliada + 1) : undefined}
          posicion={{ actual: fotoAmpliada + 1, total: images.length }}
        />
      )}
    </div>
  );
};

export default VariantImageManager;
